/**
 * RecipeModal Component
 *
 * Modal for creating and editing process recipes (spawn, liquid culture, substrate).
 * Allows selecting inventory items and specifying quantities per production unit.
 */

import { useState, useEffect, useMemo, useCallback } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useStrains } from '@/hooks/useLC'
import { useInventoryItems } from '@/hooks/useInventoryItems'
import { useProcessRecipeMutations } from '@/hooks/useProcessRecipes'
import InventoryItemSelector from './InventoryItemSelector'
import RecipeIngredientRow from './RecipeIngredientRow'
import { Plus, Loader2, BookOpen } from 'lucide-react'

// Recipe type configuration
const RECIPE_TYPE_CONFIG = {
  spawn: {
    title: 'Spawn Recipe',
    perUnitLabel: 'per bag',
    defaultUnit: 'per_bag'
  },
  liquid_culture: {
    title: 'Liquid Culture Recipe',
    perUnitLabel: 'per jar',
    defaultUnit: 'per_jar'
  },
  substrate: {
    title: 'Substrate Recipe',
    perUnitLabel: 'per block',
    defaultUnit: 'per_block'
  },
  block: {
    title: 'Block Recipe',
    perUnitLabel: 'per block',
    defaultUnit: 'per_block'
  }
}

export default function RecipeModal({
  open,
  onOpenChange,
  recipeType = 'spawn',
  existingRecipe = null,
  strainId = null,
  onSave,
  onCancel
}) {
  const isEditing = !!existingRecipe

  // Form state
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [selectedStrainId, setSelectedStrainId] = useState(strainId || '__generic__')
  const [ingredients, setIngredients] = useState([]) // { itemId, quantity }
  const [showItemSelector, setShowItemSelector] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState(null)

  // Hooks
  const { strains } = useStrains()
  const { allItems } = useInventoryItems({ activeOnly: true })
  const { createRecipe, updateRecipe } = useProcessRecipeMutations()

  // Get config for recipe type
  const config = RECIPE_TYPE_CONFIG[recipeType] || RECIPE_TYPE_CONFIG.spawn

  // Create lookup map for inventory items
  const itemsMap = useMemo(() => {
    const map = {}
    for (const item of allItems) {
      map[item.id] = item
    }
    return map
  }, [allItems])

  // Reset form when modal opens or recipe changes
  useEffect(() => {
    if (open) {
      if (existingRecipe) {
        setName(existingRecipe.name || '')
        setDescription(existingRecipe.description || '')
        setSelectedStrainId(existingRecipe.strain_id || '__generic__')

        // Convert existing lines to ingredients format
        const existingIngredients = (existingRecipe.lines || []).map(line => ({
          itemId: line.inventory_item_id,
          quantity: line.quantity || 0
        }))
        setIngredients(existingIngredients)
      } else {
        setName('')
        setDescription('')
        setSelectedStrainId(strainId || '__generic__')
        setIngredients([])
      }
      setError(null)
    }
  }, [open, existingRecipe, strainId])

  // Handle item selection from selector
  const handleItemsSelected = useCallback((selectedIds) => {
    // Merge new selections with existing ingredients
    const existingIds = new Set(ingredients.map(i => i.itemId))

    // Add new items
    const newIngredients = [...ingredients]
    for (const id of selectedIds) {
      if (!existingIds.has(id)) {
        newIngredients.push({ itemId: id, quantity: 0 })
      }
    }

    // Remove unselected items
    const selectedSet = new Set(selectedIds)
    const filteredIngredients = newIngredients.filter(i => selectedSet.has(i.itemId))

    setIngredients(filteredIngredients)
    setShowItemSelector(false)
  }, [ingredients])

  // Handle quantity change for ingredient
  const handleQuantityChange = useCallback((itemId, quantity) => {
    setIngredients(prev =>
      prev.map(i => i.itemId === itemId ? { ...i, quantity } : i)
    )
  }, [])

  // Handle remove ingredient
  const handleRemoveIngredient = useCallback((itemId) => {
    setIngredients(prev => prev.filter(i => i.itemId !== itemId))
  }, [])

  // Handle save
  const handleSave = async () => {
    // Validation
    if (!name.trim()) {
      setError('Recipe name is required')
      return
    }

    if (ingredients.length === 0) {
      setError('At least one ingredient is required')
      return
    }

    // Check all quantities are set
    const missingQuantities = ingredients.filter(i => !i.quantity || i.quantity <= 0)
    if (missingQuantities.length > 0) {
      setError('All ingredients must have a quantity greater than 0')
      return
    }

    setIsSaving(true)
    setError(null)

    try {
      const recipeData = {
        name: name.trim(),
        description: description.trim() || null,
        recipe_type: recipeType,
        strain_id: selectedStrainId === '__generic__' ? null : selectedStrainId,
        lines: ingredients.map((ing, index) => {
          const item = itemsMap[ing.itemId]
          return {
            inventory_item_id: ing.itemId,
            quantity: ing.quantity,
            unit: item?.unit_of_measure || 'unit',
            per_unit: config.defaultUnit,
            sequence_order: index + 1,
            is_optional: false
          }
        })
      }

      let savedRecipe
      if (isEditing && existingRecipe?.id) {
        savedRecipe = await updateRecipe(existingRecipe.id, recipeData)
      } else {
        savedRecipe = await createRecipe(recipeData)
      }

      onSave?.(savedRecipe)
    } catch (err) {
      console.error('[RecipeModal] Save error:', err)
      setError(err.message || 'Failed to save recipe')
    } finally {
      setIsSaving(false)
    }
  }

  // Handle cancel
  const handleCancel = () => {
    onCancel?.()
    onOpenChange(false)
  }

  // Get selected item IDs for selector
  const selectedItemIds = ingredients.map(i => i.itemId)

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5" />
              {isEditing ? `Edit ${config.title}` : `Create ${config.title}`}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto min-h-0 space-y-4 -mx-6 px-6 py-2">
            {/* Recipe Name */}
            <div className="space-y-2">
              <Label htmlFor="recipe-name">
                Recipe Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="recipe-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Standard Millet Spawn"
                disabled={isSaving}
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="recipe-description">
                Description <span className="text-muted-foreground">(optional)</span>
              </Label>
              <Textarea
                id="recipe-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Add notes about this recipe..."
                rows={2}
                disabled={isSaving}
              />
            </div>

            {/* Strain Selection */}
            <div className="space-y-2">
              <Label htmlFor="recipe-strain">
                Strain <span className="text-muted-foreground">(optional)</span>
              </Label>
              <Select
                value={selectedStrainId}
                onValueChange={setSelectedStrainId}
                disabled={isSaving}
              >
                <SelectTrigger id="recipe-strain">
                  <SelectValue placeholder="Select strain..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__generic__">
                    All strains (generic recipe)
                  </SelectItem>
                  {strains.map(strain => (
                    <SelectItem key={strain.id} value={strain.id}>
                      {strain.name} ({strain.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Ingredients Section */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Ingredients</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowItemSelector(true)}
                  disabled={isSaving}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Items
                </Button>
              </div>

              {ingredients.length === 0 ? (
                <div className="text-center py-6 border-2 border-dashed rounded-md">
                  <p className="text-sm text-muted-foreground">
                    No ingredients added yet
                  </p>
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    onClick={() => setShowItemSelector(true)}
                    disabled={isSaving}
                  >
                    Click to add items
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {ingredients.map(ing => {
                    const item = itemsMap[ing.itemId]
                    if (!item) return null

                    return (
                      <RecipeIngredientRow
                        key={ing.itemId}
                        item={item}
                        quantity={ing.quantity}
                        onQuantityChange={(qty) => handleQuantityChange(ing.itemId, qty)}
                        onRemove={() => handleRemoveIngredient(ing.itemId)}
                        disabled={isSaving}
                        perUnitLabel={config.perUnitLabel}
                      />
                    )
                  })}
                </div>
              )}

              {ingredients.length > 0 && (
                <p className="text-xs text-muted-foreground text-right">
                  Quantities are {config.perUnitLabel}
                </p>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}
          </div>

          <DialogFooter className="border-t pt-4 mt-2">
            <Button variant="outline" onClick={handleCancel} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {isEditing ? 'Update Recipe' : 'Save Recipe'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inventory Item Selector Modal */}
      <InventoryItemSelector
        open={showItemSelector}
        onOpenChange={setShowItemSelector}
        selectedItems={selectedItemIds}
        onAccept={handleItemsSelected}
        onCancel={() => setShowItemSelector(false)}
      />
    </>
  )
}
